import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { LogOut, ArrowLeft, Trash2, Plus } from "lucide-react";
import { LanguageProvider, useLanguage, BRAND } from "@/i18n";
import { photoUrl } from "@/photos";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHOTO_SLOTS = [
  { slot: "hero", label: "Foto principal (inicio)" },
  { slot: "room1", label: "Recámara Principal" },
  { slot: "room2", label: "Recámara Jardín" },
  { slot: "room3", label: "Recámara Estudio" },
  { slot: "location", label: "Galería / zona" },
];

const AdminInner = () => {
  const [token, setToken] = useState(() => localStorage.getItem("cs_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [photos, setPhotos] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/photos`)
      .then((r) => setPhotos(r.data))
      .catch(() => setPhotos(null));
    axios
      .get(`${API}/gallery`)
      .then((r) => setGallery(r.data))
      .catch(() => setGallery([]));
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("cs_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authHeaders);
    } catch {}
    localStorage.removeItem("cs_admin_token");
    setToken(null);
  };

  const uploadPhoto = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/photos/${slot}`, fd, authHeaders);
      const { data } = await axios.get(`${API}/photos`);
      setPhotos(data);
      toast.success("Foto actualizada");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo subir la foto");
    } finally {
      setUploading(null);
    }
  };

  const uploadGalleryPhoto = async (file) => {
    if (!file) return;
    setUploading("gallery");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/gallery`, fd, authHeaders);
      setGallery((prev) => [...prev, data]);
      toast.success("Foto agregada a la galería");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo subir la foto");
    } finally {
      setUploading(null);
    }
  };

  const deleteGalleryPhoto = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, authHeaders);
      setGallery((prev) => prev.filter((g) => g.id !== id));
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch {
      setError("No se pudo procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  if (!token && showForgot) {
    return (
      <div className="min-h-screen bg-[#F8F5EF] flex items-center justify-center px-6">
        <form
          onSubmit={handleForgot}
          data-testid="forgot-password-form"
          className="w-full max-w-sm bg-white border border-[#1A1A1A]/10 p-10 rounded-xl shadow-xl shadow-black/5"
        >
          <h1 className="font-display text-3xl mb-4">Restablecer contraseña</h1>
          {forgotSent ? (
            <p className="text-sm text-[#36422D] leading-relaxed" data-testid="forgot-success-message">
              Si el correo existe, te llegará un enlace para restablecer tu contraseña en los próximos minutos. Revisa tu bandeja de entrada y la carpeta de spam.
            </p>
          ) : (
            <>
              <p className="text-sm text-[#4A4D42] mb-6 leading-relaxed">
                Escribe tu correo de administrador y te generamos un enlace para crear una contraseña nueva.
              </p>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#4A4D42] mb-2">Email</label>
              <input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mb-6 px-4 py-3 border border-[#1A1A1A]/15 rounded-md bg-[#F8F5EF]/50 focus:outline-none focus:ring-1 focus:ring-[#B34A31]"
              />
              {error && (
                <p data-testid="forgot-error" className="text-sm text-[#B34A31] mb-4">
                  {error}
                </p>
              )}
              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#B34A31] text-white rounded-md hover:bg-[#9E4731] transition-colors disabled:opacity-50"
              >
                {loading ? "Enviando…" : "Enviar enlace"}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setShowForgot(false);
              setForgotSent(false);
              setError("");
            }}
            className="mt-6 flex items-center gap-2 text-xs text-[#4A4D42] hover:text-[#B34A31] transition-colors"
            data-testid="forgot-back-login"
          >
            <ArrowLeft className="w-3 h-3" /> Volver a iniciar sesión
          </button>
        </form>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8F5EF] flex items-center justify-center px-6">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white border border-[#1A1A1A]/10 p-10 rounded-xl shadow-xl shadow-black/5"
        >
          <img src="/logo.png" alt="ABAITA by Yesod Haolam" className="h-20 w-auto object-contain mb-6" />
          <h1 className="font-display text-3xl mb-8">Panel de administrador</h1>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#4A4D42] mb-2">Email</label>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-5 px-4 py-3 border border-[#1A1A1A]/15 rounded-md bg-[#F8F5EF]/50 focus:outline-none focus:ring-1 focus:ring-[#B34A31]"
          />
          <label className="block text-xs uppercase tracking-[0.2em] text-[#4A4D42] mb-2">Contraseña</label>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-6 px-4 py-3 border border-[#1A1A1A]/15 rounded-md bg-[#F8F5EF]/50 focus:outline-none focus:ring-1 focus:ring-[#B34A31]"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#B34A31] mb-4">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#B34A31] text-white rounded-md hover:bg-[#9E4731] transition-colors disabled:opacity-50"
          >
            {loading ? "Entrando…" : "Entrar"}
          </button>
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => setShowForgot(true)}
            className="mt-4 w-full text-center text-xs text-[#4A4D42] hover:text-[#B34A31] transition-colors"
          >
            Olvidé mi contraseña
          </button>
          <Link to="/" className="mt-6 flex items-center gap-2 text-xs text-[#4A4D42] hover:text-[#B34A31] transition-colors" data-testid="admin-back-home">
            <ArrowLeft className="w-3 h-3" /> Volver al sitio
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-dashboard">
      <header className="border-b border-[#1A1A1A]/10 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#9E4731]">{BRAND}</p>
            <h1 className="font-display text-2xl">Panel de administración</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-[#4A4D42] hover:text-[#B34A31] transition-colors" data-testid="admin-view-site">
              Ver sitio
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs px-4 py-2 border border-[#1A1A1A]/15 rounded-full hover:bg-[#1A1A1A]/5 transition-colors"
            >
              <LogOut className="w-3 h-3" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <section data-testid="admin-photos-section">
          <h2 className="font-display text-2xl mb-2">Fotos del sitio</h2>
          <p className="text-sm text-[#4A4D42] mb-8 max-w-xl">
            Sube tus propias fotos para cada espacio. Se ven al instante en la página. Acepta fotos de tu celular (JPG, PNG o HEIC) de hasta 15 MB.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PHOTO_SLOTS.map(({ slot, label }) => (
              <div key={slot} className="bg-white border border-[#1A1A1A]/10 rounded-xl overflow-hidden" data-testid={`admin-photo-${slot}`}>
                <div className="aspect-[4/3] bg-[#EAE4D9]">
                  <img src={photoUrl(photos ?? {}, slot)} alt={label} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{label}</p>
                  <label
                    data-testid={`admin-photo-upload-${slot}`}
                    className={`shrink-0 text-xs px-4 py-2 rounded-full cursor-pointer transition-colors ${
                      uploading === slot
                        ? "bg-[#1A1A1A]/10 text-[#4A4D42]"
                        : "bg-[#36422D] text-white hover:bg-[#2a3323]"
                    }`}
                  >
                    {uploading === slot ? "Subiendo…" : "Cambiar foto"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading === slot}
                      onChange={(e) => {
                        uploadPhoto(slot, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="admin-gallery-section">
          <h2 className="font-display text-2xl mb-2">Galería</h2>
          <p className="text-sm text-[#4A4D42] mb-8 max-w-xl">
            Agrega todas las fotos que quieras a la tira de galería del sitio. Si no agregas ninguna, se muestran las 5 fotos principales.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((g) => (
              <div key={g.id} className="relative group rounded-xl overflow-hidden border border-[#1A1A1A]/10 bg-[#EAE4D9]" data-testid={`admin-gallery-photo-${g.id}`}>
                <img src={`${API}/files/${g.storage_path}`} alt="Galería" className="w-full aspect-square object-cover" />
                <button
                  data-testid={`admin-gallery-delete-${g.id}`}
                  onClick={() => deleteGalleryPhoto(g.id)}
                  className="absolute top-2 right-2 p-2 rounded-full bg-white/90 text-[#B34A31] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                  aria-label="Eliminar foto"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <label
              data-testid="admin-gallery-add"
              className={`aspect-square rounded-xl border-2 border-dashed border-[#1A1A1A]/15 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
                uploading === "gallery" ? "opacity-50" : "hover:border-[#B34A31]/50 hover:bg-[#B34A31]/5"
              }`}
            >
              <Plus className="w-6 h-6 text-[#4A4D42]" />
              <span className="text-xs text-[#4A4D42]">{uploading === "gallery" ? "Subiendo…" : "Agregar foto"}</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading === "gallery"}
                onChange={(e) => {
                  uploadGalleryPhoto(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </section>
      </main>
    </div>
  );
};

export function AdminReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1 !== pw2) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw1 });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No se pudo restablecer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F5EF] flex items-center justify-center px-6">
      <form
        onSubmit={handleReset}
        data-testid="reset-password-form"
        className="w-full max-w-sm bg-white border border-[#1A1A1A]/10 p-10 rounded-xl shadow-xl shadow-black/5"
      >
        <h1 className="font-display text-3xl mb-6">Nueva contraseña</h1>
        {done ? (
          <>
            <p className="text-sm text-[#36422D] leading-relaxed mb-6" data-testid="reset-success-message">
              Tu contraseña se actualizó correctamente.
            </p>
            <Link
              to="/admin"
              data-testid="reset-goto-login"
              className="block w-full py-3 bg-[#B34A31] text-white rounded-md text-center hover:bg-[#9E4731] transition-colors"
            >
              Ir a iniciar sesión
            </Link>
          </>
        ) : (
          <>
            {!token && (
              <p className="text-sm text-[#B34A31] mb-4" data-testid="reset-no-token">
                El enlace no es válido. Solicita uno nuevo desde "Olvidé mi contraseña".
              </p>
            )}
            <label className="block text-xs uppercase tracking-[0.2em] text-[#4A4D42] mb-2">Contraseña nueva</label>
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={8}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="w-full mb-5 px-4 py-3 border border-[#1A1A1A]/15 rounded-md bg-[#F8F5EF]/50 focus:outline-none focus:ring-1 focus:ring-[#B34A31]"
            />
            <label className="block text-xs uppercase tracking-[0.2em] text-[#4A4D42] mb-2">Repite la contraseña</label>
            <input
              data-testid="reset-password-confirm-input"
              type="password"
              required
              minLength={8}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full mb-6 px-4 py-3 border border-[#1A1A1A]/15 rounded-md bg-[#F8F5EF]/50 focus:outline-none focus:ring-1 focus:ring-[#B34A31]"
            />
            {error && (
              <p data-testid="reset-error" className="text-sm text-[#B34A31] mb-4">
                {error}
              </p>
            )}
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 bg-[#B34A31] text-white rounded-md hover:bg-[#9E4731] transition-colors disabled:opacity-50"
            >
              {loading ? "Guardando…" : "Guardar contraseña"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

export default function Admin() {
  return (
    <LanguageProvider>
      <Toaster position="top-center" richColors />
      <AdminInner />
    </LanguageProvider>
  );
}
